import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { AggregatedQto } from "../../../../integrations/qto/types";
import { clearAtoTokens, getValidAtoAccessToken } from "../../../../integrations/qto/sources/ato/auth";
import { loadAtoAggregatedForPackage } from "../../../../integrations/qto/sources/ato/api";
import { UnauthorizedError } from "../../../../errors";
import type { AtoAccProject, AtoHub, AtoPackage } from "../../../../integrations/qto/sources/ato/types";
import { workbookCache } from "../../../../excel/workbook/cache";
import { useBanner } from "../../../context/BannerContext";
import { ATO_LOAD_ERROR, ATO_REFRESHED, ATO_REFRESH_ERROR } from "../../../constants";

export type AtoState = "login" | "selector" | "loading" | "connected";

interface AtoContextType {
  atoState: AtoState;
  setAtoState: (state: AtoState) => void;
  isAtoAuthenticated: boolean;
  atoHub: AtoHub | null;
  atoProject: AtoAccProject | null;
  atoPackage: AtoPackage | null;
  /** Raw takeoff item count (Total chip). */
  atoTakeoffItemCount: number | null;
  atoAggregatedQtos: AggregatedQto[] | null;
  atoLastUpdated: string | null;
  isRefreshing: boolean;
  connectAtoFromSelection: (hub: AtoHub, project: AtoAccProject, pkg: AtoPackage) => Promise<void>;
  refreshTakeoffs: () => Promise<void>;
  initializeAtoState: () => Promise<void>;
  clearAtoSession: () => Promise<void>;
  disconnectAtoToSelector: () => Promise<void>;
  returnToSelectorWithHub: (hub: AtoHub) => void;
  selectorPrefillHub: AtoHub | null;
  clearSelectorPrefill: () => void;
}

const AtoContext = createContext<AtoContextType | undefined>(undefined);

export const AtoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { showBanner } = useBanner();
  const [atoState, setAtoState] = useState<AtoState>("login");
  const [isAtoAuthenticated, setIsAtoAuthenticated] = useState(false);
  const [atoHub, setAtoHub] = useState<AtoHub | null>(null);
  const [atoProject, setAtoProject] = useState<AtoAccProject | null>(null);
  const [atoPackage, setAtoPackage] = useState<AtoPackage | null>(null);
  const [atoTakeoffItemCount, setAtoTakeoffItemCount] = useState<number | null>(null);
  const [atoAggregatedQtos, setAtoAggregatedQtos] = useState<AggregatedQto[] | null>(null);
  const [atoLastUpdated, setAtoLastUpdated] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectorPrefillHub, setSelectorPrefillHub] = useState<AtoHub | null>(null);

  const isRefreshingRef = useRef(false);
  const aggregatedRef = useRef(atoAggregatedQtos);
  const itemCountRef = useRef(atoTakeoffItemCount);
  aggregatedRef.current = atoAggregatedQtos;
  itemCountRef.current = atoTakeoffItemCount;

  const clearAtoSession = useCallback(async () => {
    clearAtoTokens();
    setIsAtoAuthenticated(false);
    setAtoHub(null);
    setAtoProject(null);
    setAtoPackage(null);
    setAtoTakeoffItemCount(null);
    setAtoAggregatedQtos(null);
    setAtoLastUpdated(null);
    setAtoState("login");
    await workbookCache.delete("atoHubId");
    await workbookCache.delete("atoHubName");
    await workbookCache.delete("atoProjectId");
    await workbookCache.delete("atoProjectName");
    await workbookCache.delete("atoPackageId");
    await workbookCache.delete("atoPackageName");
  }, []);

  const loadPackageData = useCallback(
    async (projectId: string, packageId: string): Promise<{ ok: true } | { ok: false; unauthorized: boolean }> => {
      const result = await loadAtoAggregatedForPackage(projectId, packageId);
      if (!result.success || result.data == null) {
        const unauthorized = !result.success && result.error instanceof UnauthorizedError;
        if (unauthorized) {
          await clearAtoSession();
        }
        return { ok: false, unauthorized };
      }
      setAtoAggregatedQtos(result.data.aggregated);
      setAtoTakeoffItemCount(result.data.aggregated.length);
      setAtoLastUpdated(new Date().toISOString());
      return { ok: true };
    },
    [clearAtoSession]
  );

  const connectAtoFromSelection = useCallback(
    async (hub: AtoHub, project: AtoAccProject, pkg: AtoPackage) => {
      setAtoState("loading");
      setAtoHub(hub);
      setAtoProject(project);
      setAtoPackage(pkg);

      try {
        const token = await getValidAtoAccessToken();
        if (!token) {
          await clearAtoSession();
          return;
        }

        const packageLoad = await loadPackageData(project.id, pkg.id);
        if (!packageLoad.ok) {
          if (packageLoad.unauthorized) {
            return;
          }
          showBanner(ATO_LOAD_ERROR, "error");
          setAtoState("selector");
          return;
        }

        await workbookCache.set({
          atoHubId: hub.id,
          atoHubName: hub.name,
          atoProjectId: project.id,
          atoProjectName: project.name,
          atoPackageId: pkg.id,
          atoPackageName: pkg.name,
        });
        setAtoState("connected");
      } catch {
        showBanner(ATO_LOAD_ERROR, "error");
        setAtoState("selector");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clearAtoSession, loadPackageData]
  );

  const initializeAtoState = useCallback(async () => {
    const token = await getValidAtoAccessToken();
    if (!token) {
      setIsAtoAuthenticated(false);
      setAtoState("login");
      return;
    }

    setIsAtoAuthenticated(true);

    const cachedHubId = await workbookCache.get("atoHubId");
    const cachedHubName = await workbookCache.get("atoHubName");
    const cachedProjectId = await workbookCache.get("atoProjectId");
    const cachedProjectName = await workbookCache.get("atoProjectName");
    const cachedPackageId = await workbookCache.get("atoPackageId");
    const cachedPackageName = await workbookCache.get("atoPackageName");

    if (
      typeof cachedHubId !== "string" ||
      !cachedHubId ||
      typeof cachedProjectId !== "string" ||
      !cachedProjectId ||
      typeof cachedPackageId !== "string" ||
      !cachedPackageId
    ) {
      setAtoState("selector");
      return;
    }

    setAtoState("loading");

    const packageLoad = await loadPackageData(cachedProjectId, cachedPackageId);
    if (!packageLoad.ok) {
      if (!packageLoad.unauthorized) {
        setAtoState("selector");
      }
      return;
    }

    const asName = (v: unknown) => (typeof v === "string" ? v : "");

    setAtoHub({ id: cachedHubId, name: asName(cachedHubName) });
    setAtoProject({ id: cachedProjectId, name: asName(cachedProjectName) });
    setAtoPackage({ id: cachedPackageId, name: asName(cachedPackageName) });
    setAtoState("connected");
  }, [loadPackageData]);

  const refreshTakeoffs = useCallback(async () => {
    if (isRefreshingRef.current) return;
    const projectId = atoProject?.id;
    const packageId = atoPackage?.id;
    if (!projectId || !packageId) return;

    isRefreshingRef.current = true;
    setIsRefreshing(true);
    const previousAgg = aggregatedRef.current;
    const previousCount = itemCountRef.current;
    setAtoAggregatedQtos(null);
    setAtoTakeoffItemCount(null);

    try {
      const packageLoad = await loadPackageData(projectId, packageId);
      if (!packageLoad.ok) {
        if (packageLoad.unauthorized) {
          return;
        }
        setAtoAggregatedQtos(previousAgg ?? []);
        setAtoTakeoffItemCount(previousCount ?? 0);
        showBanner(ATO_REFRESH_ERROR, "error");
      } else {
        showBanner(ATO_REFRESHED, "success");
      }
    } catch {
      setAtoAggregatedQtos(previousAgg ?? []);
      setAtoTakeoffItemCount(previousCount ?? 0);
      showBanner(ATO_REFRESH_ERROR, "error");
    } finally {
      isRefreshingRef.current = false;
      setIsRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atoPackage, atoProject, loadPackageData]);

  const disconnectAtoToSelector = useCallback(async () => {
    setSelectorPrefillHub(null);
    setAtoHub(null);
    setAtoProject(null);
    setAtoPackage(null);
    setAtoTakeoffItemCount(null);
    setAtoAggregatedQtos(null);
    setAtoLastUpdated(null);
    setAtoState("selector");
    await workbookCache.delete("atoHubId");
    await workbookCache.delete("atoHubName");
    await workbookCache.delete("atoProjectId");
    await workbookCache.delete("atoProjectName");
    await workbookCache.delete("atoPackageId");
    await workbookCache.delete("atoPackageName");
  }, []);

  const returnToSelectorWithHub = useCallback((hub: AtoHub) => {
    setSelectorPrefillHub(hub);
    setAtoState("selector");
  }, []);

  const clearSelectorPrefill = useCallback(() => {
    setSelectorPrefillHub(null);
  }, []);

  useEffect(() => {
    void initializeAtoState();
  }, [initializeAtoState]);

  return (
    <AtoContext.Provider
      value={{
        atoState,
        setAtoState,
        isAtoAuthenticated,
        atoHub,
        atoProject,
        atoPackage,
        atoTakeoffItemCount,
        atoAggregatedQtos,
        atoLastUpdated,
        isRefreshing,
        connectAtoFromSelection,
        refreshTakeoffs,
        initializeAtoState,
        clearAtoSession,
        disconnectAtoToSelector,
        returnToSelectorWithHub,
        selectorPrefillHub,
        clearSelectorPrefill,
      }}
    >
      {children}
    </AtoContext.Provider>
  );
};

export const useAto = () => {
  const ctx = useContext(AtoContext);
  if (!ctx) {
    throw new Error("useAto must be used within an AtoProvider");
  }
  return ctx;
};
